import { useState } from 'react';
import { Gem } from 'lucide-react';
import { products } from '../data/products';
import { ProductCard } from '../components/ProductCard';


const categories = ['Tous', 'Bagues', 'Colliers', 'Bracelets', "Boucles d'oreilles"];
const materials = ['Tous', 'Or', 'Argent', 'Acier inoxydable', 'Plaqué or'];
const colors = ['Tous', 'Doré', 'Argenté'];

export function BoutiquePage() {

  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('Tous');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [activeMaterial, setActiveMaterial] = useState('Tous');
  const [activeColor, setActiveColor] = useState('Tous');

  const filtered = products.filter((p) => {
    if (activeCategory !== 'Tous' && p.category !== activeCategory) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (activeMaterial !== 'Tous' && !p.material.toLowerCase().includes(activeMaterial.toLowerCase())) return false;
    if (activeColor !== 'Tous') {
      if (activeColor === 'Doré' && !/dor[ée]|or|plaqué or/i.test(p.material + ' ' + p.name)) return false;
      if (activeColor === 'Argenté' && !/argent/i.test(p.material + ' ' + p.name)) return false;
    }
    if (minPrice && p.price < Number(minPrice)) return false;
    if (maxPrice && p.price > Number(maxPrice)) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative py-20 gradient-bg tattoo-pattern">
        <div className="absolute inset-0 fine-grid-pattern z-10 pointer-events-none" />
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-6">
            <Gem className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-textPrimary mb-6 leading-tight">
            Boutique Bijoux
          </h1>
          <p className="text-lg sm:text-xl text-textSecondary max-w-3xl mx-auto leading-relaxed">
            Découvrez notre collection de bijoux soigneusement sélectionnés pour sublimer votre style.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filtres latéraux */}
          <aside className="w-full lg:w-72 bg-white rounded-2xl shadow-md p-6 mb-8 lg:mb-0 flex-shrink-0">
            <h2 className="text-lg font-semibold mb-4 text-textPrimary">Filtres</h2>
            {/* Recherche */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-textSecondary mb-1">Recherche</label>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-primary-300 outline-none"
                placeholder="Nom du bijou..."
              />
            </div>
            {/* Catégorie */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-textSecondary mb-1">Catégorie</label>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                      activeCategory === cat
                        ? 'bg-primary-300 text-white shadow-md'
                        : 'bg-gray-100 text-textSecondary hover:bg-primary-50 hover:text-primary-500 shadow-sm'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
            {/* Prix */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-textSecondary mb-1">Prix (€)</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min={0}
                  value={minPrice}
                  onChange={e => setMinPrice(e.target.value)}
                  className="w-1/2 px-3 py-2 rounded-lg border border-gray-200 focus:border-primary-300 outline-none"
                  placeholder="Min"
                />
                <input
                  type="number"
                  min={0}
                  value={maxPrice}
                  onChange={e => setMaxPrice(e.target.value)}
                  className="w-1/2 px-3 py-2 rounded-lg border border-gray-200 focus:border-primary-300 outline-none"
                  placeholder="Max"
                />
              </div>
            </div>
            {/* Matière */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-textSecondary mb-1">Matière</label>
              <div className="flex flex-wrap gap-2">
                {materials.map((mat) => (
                  <button
                    key={mat}
                    onClick={() => setActiveMaterial(mat)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                      activeMaterial === mat
                        ? 'bg-primary-300 text-white shadow-md'
                        : 'bg-gray-100 text-textSecondary hover:bg-primary-50 hover:text-primary-500 shadow-sm'
                    }`}
                  >
                    {mat}
                  </button>
                ))}
              </div>
            </div>
            {/* Couleur */}
            <div className="mb-2">
              <label className="block text-sm font-medium text-textSecondary mb-1">Couleur</label>
              <div className="flex flex-wrap gap-2">
                {colors.map((col) => (
                  <button
                    key={col}
                    onClick={() => setActiveColor(col)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                      activeColor === col
                        ? 'bg-primary-300 text-white shadow-md'
                        : 'bg-gray-100 text-textSecondary hover:bg-primary-50 hover:text-primary-500 shadow-sm'
                    }`}
                  >
                    {col}
                  </button>
                ))}
              </div>
            </div>
          </aside>
          {/* Grille produits */}
          <div className="flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
            {filtered.length === 0 && (
              <div className="text-center py-16 text-textSecondary">
                Aucun produit ne correspond à vos filtres.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
